/**
 * Migration cost bands, labelled in the project currency. A copy of backend
 * insights/costBands.ts (COST_BANDS, LEGACY_COST_BANDS, costBandLabel); cost-bands.test.ts keeps the two in step.
 */
export const COST_BANDS = [
  'Range_under_10000',
  'Range_10000_25000',
  'Range_25000_50000',
  'Range_50000_100000',
  'Range_100000_250000',
  'Range_250000_500000',
  'Range_500000_1000000',
  'Range_1000000_2500000',
  'Range_2500000_5000000',
  'Range_5000000_10000000',
  'Range_above_10000000',
] as const;
export type CostBandCode = (typeof COST_BANDS)[number];

/** The original template's five bands: still valid values, still readable. */
export const LEGACY_COST_BANDS = [
  'Range_0_5000', 'Range_5001_20000', 'Range_20001_50000', 'Range_50001_100000', 'Range_more_than_100000',
] as const;

/** Rupees in the short form a reader uses: 10K, 2.5L, 1Cr. */
function inrShort(n: number): string {
  const t = (x: number) => String(Math.round(x * 100) / 100);
  if (n >= 1e7) return `${t(n / 1e7)}Cr`;
  if (n >= 1e5) return `${t(n / 1e5)}L`;
  if (n >= 1e3) return `${t(n / 1e3)}K`;
  return String(n);
}
/** Other currencies: 10k, 2.5m. */
function plainShort(n: number): string {
  const t = (x: number) => String(Math.round(x * 100) / 100);
  if (n >= 1e9) return `${t(n / 1e9)}bn`;
  if (n >= 1e6) return `${t(n / 1e6)}m`;
  if (n >= 1e3) return `${t(n / 1e3)}k`;
  return String(n);
}

/**
 * A band as a reader sees it, in the project currency: "₹10K–25K", "Above ₹1Cr (needs a quote)",
 * "USD 10k–25k". Legacy codes in rupees read as the lakh ranges they stood for. Unknown codes are
 * returned as they are.
 */
export function costBandLabel(code: string | null | undefined, currency: string | null = null): string {
  if (!code) return '';
  const cur = (currency ?? '').toUpperCase();
  const inr = cur === 'INR';
  const fmt = (n: number) => (inr ? `₹${inrShort(n)}` : `${cur ? `${cur} ` : ''}${plainShort(n)}`);
  const fmt2 = (a: number, b: number) => (inr ? `₹${inrShort(a)}–${inrShort(b)}` : `${cur ? `${cur} ` : ''}${plainShort(a)}–${plainShort(b)}`);
  const c = code.replace(/^Range_/i, '');
  if (inr) {
    const legacy: Record<string, string> = {
      '0_5000': '₹0.5L–1L', '5001_20000': '₹1L–2L', '20001_50000': '₹2L–3.5L', '50001_100000': '₹3.5L–5L',
      more_than_100000: 'Above ₹5L (needs a quote)',
    };
    if (legacy[c.toLowerCase()]) return legacy[c.toLowerCase()]!;
  }
  let m = /^(\d+)_(\d+)$/.exec(c);
  if (m) return fmt2(Number(m[1]), Number(m[2]));
  m = /^(?:under|below|less_than)_(\d+)$/i.exec(c);
  if (m) return `Under ${fmt(Number(m[1]))}`;
  m = /^(?:above|over|more_than)_(\d+)$/i.exec(c);
  if (m) return `Above ${fmt(Number(m[1]))} (needs a quote)`;
  return code;
}
